import React from "react"
import {
	AccountType,
	AssetProvenience,
	AssetWithImage,
	CreateAssetRequest,
	CreateAssetResponse,
	ListLandlordsProjection,
	PaginatedResponse,
	SessionResponse,
} from "models"
import { PageSectionTitle } from "components/commons"
import { i18n } from "@lingui/core"
import { AssetListOrigin } from "components/dashboard/dashboard-assets-list/model"
import DashboardAssetListItem from "./dashboard-asset-list-item"
import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Typography,
} from "@mui/material"
import { mdiPlusBox, mdiInformationOutline } from "@mdi/js"
import Icon from "@mdi/react"
import { Trans } from "@lingui/react"
import { t } from "@lingui/macro"
import { CreateAssetModal } from "components/asset/asset-details"
import {
	ErrorResponse,
	RestResponse,
} from "@sinossi/mates-react-library"
import { AssetService } from "services/asset.service"
import withUseRouter, { UseRouterParams } from "services/router"
import {
	MatesComponent,
	MatesComponentState,
} from "components/mates-component"

interface DashboardAssetsProps extends UseRouterParams {
	assetsList: AssetWithImage[]
	likes: string[]
	origin: AssetListOrigin
	sessionData: SessionResponse
	filter?: (filter: AssetProvenience|null) => void
	refreshLikes?: () => void
}

interface DashboardAssetsState extends MatesComponentState {
	provenance: AssetProvenience | null
	openCreateNewAsset: boolean
	landlordList: PaginatedResponse<ListLandlordsProjection> | null
	loading: boolean
}

class DashboardAssetsList extends React.Component<DashboardAssetsProps, DashboardAssetsState>{

	private readonly matesComponent: MatesComponent<DashboardAssetsProps, DashboardAssetsState> = new MatesComponent(this)

	constructor(props: DashboardAssetsProps)
	{
		super(props)
		this.state = {
			...this.matesComponent.setupState(),
			provenance: null,
			openCreateNewAsset: false,
			landlordList: null,
			loading: false
		}
	}

	componentDidMount() {
		if(this.props.origin === AssetListOrigin.NEWS){
			this.getLandlord()
		}
	}

	componentDidUpdate(_prevProps: DashboardAssetsProps, prevState: DashboardAssetsState) {
		if(prevState.loading) {
			this.setState(() => ({loading: false}))
		}
	}

	private getLandlord = () => {
		AssetService.getLandlord(this.getLandlordCallback, this.getLandlordCallbackError)
	}

	private getLandlordCallback = (response: RestResponse<PaginatedResponse<ListLandlordsProjection>, ErrorResponse>): void => {

		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.setState(() => ({ loading: false, landlordList: response.data }))
	}

	private getLandlordCallbackError = (response: RestResponse<PaginatedResponse<ListLandlordsProjection>, ErrorResponse>): void => {
		this.setState(() => ({ landlordList: null, loading: false }))
		this.matesComponent.onError(response)
	}

	private createAsset = (values: any) => {
		let request: CreateAssetRequest = {
			address: values.address,
			landlordId: values.owner,
		}

		this.setState(() => ({ loading: true, openCreateNewAsset: false }))
		AssetService.createAsset(request, this.createAssetCallback, this.matesComponent.onError)
	}

	private createAssetCallback = (response: RestResponse<CreateAssetResponse, ErrorResponse>): void => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.setState(() => ({
			loading: false,
		}))

		const { navigate } = this.props
		navigate(`/asset/${response.data?.id}`)
	}

	private setProvenance = (event: any) => {
		if(this.props.filter !== undefined){
			if(event.target.value !== "empty"){
				this.setState(() => ({
					provenance: event.target.value as AssetProvenience,
					loading: true
				}), () => this.props.filter!(this.state.provenance))
			}
			else {
				this.setState(() => ({provenance: null, loading: true}), () => this.props.filter!(this.state.provenance))
			}
		}
	}

	render() {
		return (
			<>
				<PageSectionTitle title={/*i18n*/i18n._({ id: `enums.asset-list-origin.${this.props.origin}` })} />
				{
					this.props.origin === AssetListOrigin.NEWS &&
					<Box
						sx={{
							display: "flex",
							justifyContent: "right",
							width: "100%",
							gap: 3
						}}
					>
						<Button
							variant={"contained"}
							color={"info"}
							startIcon={<Icon path={mdiPlusBox} size={1} />}
							onClick={() => this.setState(() => ({openCreateNewAsset: true}))}
						>
							<Trans id={"dashboard-asset-list.add-new-asset"} message={"Aggiungi immobile"}/>
						</Button>
						<FormControl>
							<InputLabel id="provenience-filter">{t({id: "dashboard-assets-list.provenience-filter", message: "Filtra per provenienza"})}</InputLabel>
							<Select
								variant="outlined"
								labelId="provenience-filter"
								label={t({id: "dashboard-assets-list.provenience-filter", message: "Filtra per provenienza"})}
								name="provenance"
								onChange={this.setProvenance}
								value={this.state.provenance}
								sx={{width: "250px", backgroundColor: "background.paper"}}
							>
								<MenuItem value={"empty"} key={"empty-value"}>
									<Trans id={`enums.asset-provenience.empty`} message={"Nessun filtro impostato"} />
								</MenuItem>
								{Object.keys(AssetProvenience).map((key) =>
									<MenuItem value={key} key={key}>
										<Trans id={ `enums.asset-provenience.${key}`}>
										</Trans>
									</MenuItem>
								)}
							</Select>
						</FormControl>
					</Box>
				}
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 2
					}}
				>
					{
						this.state.loading &&
						<Box
							sx={{
								display: "flex",
								width: "100%",
								justifyContent: "center",
								alignItems: "center",
								height: "130px"
							}}
						>
							<CircularProgress />
						</Box>
					}
					{
						!this.state.loading && this.props.assetsList.map(asset =>
							<DashboardAssetListItem
								{...this.props}
								asset={asset}
								refreshLikes={this.props.refreshLikes}
							/>
						)
					}
					{
						!this.state.loading && this.props.assetsList.length === 0 &&
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								alignItems: "center",
								backgroundColor: "#f9fafb",
								borderRadius: "10px",
								flex: 1,
								mb: 2,
								px: 2,
								py: 3,
								width: "100%",
								minHeight: "130px",
								gap: 1
							}}
						>
							<Icon 
								path={mdiInformationOutline} 
								size={2} 
								color="#9ca3af"
							/>
							<Typography 
								variant="body1" 
								color="text.secondary"
								sx={{ textAlign: "center" }}
							>
								<Trans id={"dashboard-assets-list.empty-list"} message={"Nessun elemento presente"} />
							</Typography>
						</Box>
					}
				</Box>
				{
					this.state.openCreateNewAsset &&
					<CreateAssetModal
						closeModal={() => this.setState(() => ({openCreateNewAsset: false}))}
						isCreatingAccountLandlord={this.props.sessionData.accountType === AccountType.LANDLORD}
						submitForm={this.createAsset}
						landlordList={this.state.landlordList?.items ?? []}
					/>
				}
			</>
		)
	}
}

export default withUseRouter(DashboardAssetsList)
